import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  CardMedia,
  Modal,
  Box,
  IconButton,
  DialogTitle,
  Dialog,
  DialogContent,
} from "@mui/material";
import { CartItem, addItem, Variant } from "../store/cartSlice"; // Adjust import as needed
import { useDispatch } from "react-redux";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined"; // Import the cart icon
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ChildCareOutlinedIcon from "@mui/icons-material/ChildCareOutlined";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Close, Straighten } from "@mui/icons-material";

interface ItemTileProps {
  item: {
    id: string;
    name: string;
    type: string;
    isActive: boolean;
    isSaleActive: boolean; // Add the flag for sales
    images?: string[];
    variants: Variant[];
    sizeGuideUrl?: string; // Optional size guide URL
  };
}

const ItemTile: React.FC<ItemTileProps> = ({ item }) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState<Variant | null>(null); // Store selected variant
  const [quantity, setQuantity] = useState<number>(1); // Quantity state
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [selectedSize, setSelectedSize] = useState<string>(""); // New state to track size
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0); // Track current image index

  // Inside ItemTile Component:
  const [openSizeGuide, setOpenSizeGuide] = useState(false);

  const images = selectedVariant?.imageUrl
    ? [selectedVariant.imageUrl, ...(item.images || [])]
    : item.images || [];

  // 1. Get unique color options based on color codes
  const uniqueColors = Array.from(
    new Map(item.variants.map((v) => [v.colorCode, v])).values()
  );

  // 2. Get available sizes specifically for the currently selected color
  const availableSizesForColor = Array.from(
    new Set(
      item.variants
        .filter((variant) => variant.colorCode === selectedColor && variant.availableQuantity > 0)
        .map((variant) => variant.size)
    )
  );

  useEffect(() => {
    if (item.variants && item.variants.length > 0) {
      const defaultVariant = item.variants[0];
      setSelectedVariant(defaultVariant);
      setSelectedColor(defaultVariant.colorCode || "#FFFFFF");
      setSelectedSize(defaultVariant.size || "");
    }
  }, [item]);

  // Handle color change: switch color and pick the first available size for it
  const handleColorSelection = (colorCode: string) => {
    setSelectedColor(colorCode);

    const sizesForNewColor = item.variants.filter((v) => v.colorCode === colorCode && v.availableQuantity > 0);
    if (sizesForNewColor.length > 0) {
      const firstAvailableSize = sizesForNewColor[0];
      setSelectedSize(firstAvailableSize.size || "");
      setSelectedVariant(firstAvailableSize);
    }
    setCurrentImageIndex(0);
  };

  // Handle size change: update selected size and find the variant matching color + size
  const handleSizeSelection = (size: string) => {
    setSelectedSize(size);
    const matchingVariant = item.variants.find(
      (variant) => variant.colorCode === selectedColor && variant.size === size
    );
    setSelectedVariant(matchingVariant || null);
  };

  const handleAddItem = () => {
    if (selectedVariant) {
      const itemWithDetails: CartItem = {
        id: item.id,
        name: item.name,
        type: item.type,
        isActive: item.isActive,
        isSaleActive: item.isSaleActive,
        selectedVariant: {
          ...selectedVariant,
          quantity,
        },
      };
      dispatch(addItem(itemWithDetails));
      handleClose();
    }
  };

  const handleOpen = () => {
    setOpen(true);
    if (!selectedVariant && item.variants.length > 0) {
      setSelectedVariant(item.variants[0]); // Set the first variant as selected by default
      setSelectedColor(item.variants[0].colorCode || "#FFFFFF");
      setSelectedSize(item.variants[0].size || "");
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedVariant(item.variants[0]); // Reset color on close
    setSelectedColor(item.variants[0].colorCode || "#FFFFFF");
    setQuantity(1); // Reset quantity on close
    setCurrentImageIndex(0);
  };

  // Carousel Functions
  const handlePrevImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };

  const increaseQuantity = () => {
    if (selectedVariant && quantity < selectedVariant.availableQuantity) {
      setQuantity(quantity + 1);
    }
  };

  const decreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  return (
    <>
      <Box
        onClick={handleOpen}
        sx={{
          cursor: "pointer",
        }}
      >
        <Card
          variant="outlined"
          style={{
            margin: "10px",
            width: "320px",
            height: "370px",
            borderRadius: "20px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            overflow: "hidden",
            position: "relative",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
            backgroundColor: "rgba(255, 255, 255, 0.4)",
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: "100%",
              aspectRatio: "1 / 1", // 👈 Creates a clean, uniform square canvas across all tiles
              backgroundColor: "#efedfb", // 👈 Soft, warm off-white canvas (giving a cozy aesthetic)
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 2, // 👈 Soft inner padding keeps garments from touching card edges
              overflow: "hidden",
              borderRadius: "12px 12px 0 0", // Keeps top corners rounded to match your card
            }}
          >
            <CardMedia
              component="img"
              image={selectedVariant?.imageUrl ?? ""}
              alt={item.name}
              sx={{
                maxHeight: "85%", // 👈 Constrains the max image height so it never looks overwhelmingly big
                maxWidth: "85%",  // 👈 Keeps margins breathing around the item
                objectFit: "contain",
                transition: "transform 0.4s ease", // Smooth hover effect
                "&:hover": {
                  transform: "scale(1.04)", // Gentle zoom effect on hover
                },
              }}
            />
          </Box>
          <CardContent style={{ flexGrow: 1 }}>
            <Typography variant="h6">{item.name}</Typography>
            <Typography variant="h3">
              {selectedVariant ? (
                item.isSaleActive && selectedVariant.salePrice ? (
                  <>
                    <span style={{ textDecoration: "line-through", marginRight: "10px" }}>
                      LKR {selectedVariant.unitPrice.toFixed(2)}
                    </span>
                    <span style={{ color: "red" }}>
                      LKR {selectedVariant.salePrice.toFixed(2)}
                    </span>
                  </>
                ) : (
                  `LKR ${selectedVariant.unitPrice.toFixed(2)}`
                )
              ) : (
                "Select a variant"
              )}
            </Typography>
            <div
              style={{
                display: "flex",
                justifyContent: "right",
                padding: "0.1px",
              }}
            >
              <Button
                variant="contained"
                color="secondary"
                onClick={handleOpen}
                disabled={
                  quantity > (selectedVariant?.availableQuantity ?? 0) ||
                  !selectedVariant
                }
                style={{ marginRight: "5px" }}
              >
                <ShoppingCartOutlinedIcon sx={{ color: "#f6f5fcff" }} />
              </Button>
            </div>
          </CardContent>
        </Card>
      </Box>

      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            bgcolor: "background.paper",
            borderRadius: "20px",
            boxShadow: 24,
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: "450px" },
            maxHeight: "90vh", // Prevents the modal from exceeding the viewport height
            display: "flex",
            flexDirection: "column", // Flex container to stack elements
            outline: "none",
            overflow: "hidden" // Hides overall scrollbar to keep layout intact
          }}
        >
          {/* 1. FIXED HEADER (Title & Images) */}
          <Box sx={{ p: 3, pb: 1 }}>
            <Typography variant="h4" sx={{ mb: 1 }}>
              {item.name}
            </Typography>

            {/* Image Carousel */}
            <Box sx={{ position: "relative", width: "100%", height: "200px", mb: 1 }}>
              <img
                src={images[currentImageIndex]}
                alt={`Product preview`}
                style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: "8px" }}
              />
              {images.length > 1 && (
                <>
                  <IconButton onClick={handlePrevImage} sx={{ position: "absolute", top: "50%", left: 0, transform: "translateY(-50%)", backgroundColor: "rgba(231, 186, 209, 0.5)", color: "white", "&:hover": { backgroundColor: "rgba(231, 186, 209, 0.7)" } }}>
                    <ArrowBackIosIcon sx={{ fontSize: "1rem" }} />
                  </IconButton>
                  <IconButton onClick={handleNextImage} sx={{ position: "absolute", top: "50%", right: 0, transform: "translateY(-50%)", backgroundColor: "rgba(231, 186, 209, 0.5)", color: "white", "&:hover": { backgroundColor: "rgba(231, 186, 209, 0.7)" } }}>
                    <ArrowForwardIosIcon sx={{ fontSize: "1rem" }} />
                  </IconButton>
                </>
              )}
            </Box>
          </Box>

          {/* Color Selection */}
          {/* 2. SCROLLABLE MIDDLE CONTENT (Colors, Sizes, Material) */}
          <Box sx={{
            px: 3,
            overflowY: "auto", // Allows scrolling only within this section if needed
            flexGrow: 1,
            pb: 2,
            /* Custom styled minimalist scrollbar */
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-thumb": { backgroundColor: "#eee", borderRadius: "10px" }
          }}>
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: "bold", mb: 1, color: "#666" }}>
                <ChildCareOutlinedIcon sx={{ fontSize: "1rem" }} /> Color:
              </Typography>
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
                {uniqueColors.map((variant) => (
                  <Box
                    key={variant.colorCode}
                    onClick={() => handleColorSelection(variant.colorCode || "#FFFFFF")}
                    sx={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "50%",
                      backgroundColor: variant.colorCode,
                      cursor: "pointer",
                      border: selectedColor === variant.colorCode ? "3px solid #57b7fb" : "1px solid #ddd",
                      boxShadow: selectedColor === variant.colorCode ? "0px 0px 8px rgba(127, 190, 235, 0.6)" : "none",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        transform: "scale(1.1)",
                      }
                    }}
                    title={variant.color}
                  />
                ))}
                {selectedColor && (
                  <Typography sx={{ fontWeight: "600", fontSize: "0.8rem", color: "#fb99ca", fontStyle: "italic" }}>
                    {item.variants.find((v) => v.colorCode === selectedColor)?.color}
                  </Typography>
                )}
              </Box>
            </Box>
            {/* Minimalist Size Selector (Safe from duplicates & TS Errors) */}
            <Box sx={{ mt: 2.5 }}>
              <Typography
                variant="h3"
                sx={{
                  fontSize: "1rem",
                  fontWeight: "bold",
                  color: "#666",
                  mb: 1,
                  display: "flex",
                  alignItems: "center"
                }}
              >
                <ChildCareOutlinedIcon
                  sx={{
                    fontSize: "1rem",
                    verticalAlign: "middle",
                    marginRight: "8px",
                  }}
                />
                <strong>Size:</strong>
              </Typography>

              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {Array.from(
                  new Set(
                    item.variants
                      .filter(
                        (variant) =>
                          variant.colorCode === selectedColor &&
                          variant.availableQuantity > 0 &&
                          variant.size // 1. Ensures we only get variants that actually have a size string
                      )
                      .map((variant) => String(variant.size)) // 2. Forces conversion to string for TS safety
                  )
                ).map((size: string) => { // 3. Explicitly typed as string
                  const isSelected = selectedSize === size;
                  return (
                    <Button
                      key={size}
                      variant={isSelected ? "contained" : "outlined"}
                      onClick={() => handleSizeSelection(size)}
                      sx={{
                        minWidth: "60px",
                        height: "36px",
                        borderRadius: "18px",
                        textTransform: "none",
                        fontSize: "0.85rem",
                        fontWeight: isSelected ? "600" : "400",
                        px: 2,
                        backgroundColor: isSelected ? "#57b7fb" : "transparent",
                        color: isSelected ? "#fff" : "#555",
                        borderColor: isSelected ? "#57b7fb" : "#e0e0e0",
                        boxShadow: isSelected ? "0px 3px 6px rgba(127, 190, 235, 0.3)" : "none",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: isSelected ? "#57b7fb" : "#f5f9fc",
                          borderColor: "#57b7fb",
                        },
                      }}
                    >
                      {size}
                    </Button>
                  );
                })}
              </Box>
            </Box>

            {/* Size Guide Action Trigger - Displays only for Apparel items */}
            {item.sizeGuideUrl && (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "flex-end", // 👈 Aligns the button to the right
                  mt: 1,
                  mb: 1
                }}
              >
                <Button
                  startIcon={<Straighten sx={{ fontSize: "16px !important", color: "#7FBEEB" }} />}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevents clicking the card background action
                    setOpenSizeGuide(true);
                  }}
                  sx={{
                    textTransform: "none",
                    fontSize: "12px",
                    color: "#555",
                    padding: "2px 6px",
                    borderRadius: "12px",
                    backgroundColor: "transparent",
                    "&:hover": {
                      backgroundColor: "#e8f4fd",
                      color: '#fb99ca'
                    }
                  }}
                >
                  Size Guide
                </Button>

                {/* Size Guide Modal Popup */}
                <Dialog
                  open={openSizeGuide}
                  onClose={() => setOpenSizeGuide(false)}
                  maxWidth="sm"
                  fullWidth
                >
                  <DialogTitle sx={{ m: 0, p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="h6" sx={{ fontWeight: "bold", color: "#444" }}>
                      Size Guide — {item.name}
                    </Typography>
                    <IconButton onClick={(e) => { e.stopPropagation(); setOpenSizeGuide(false); }}>
                      <Close />
                    </IconButton>
                  </DialogTitle>

                  <DialogContent dividers sx={{ textAlign: "center", p: 2 }}>
                    <Box
                      component="img"
                      src={item.sizeGuideUrl} // 👈 Dynamically loads the specific size chart for this garment!
                      alt={`${item.name} Size Guide`}
                      sx={{ maxWidth: "100%", height: "auto", borderRadius: "8px" }}
                    />
                  </DialogContent>
                </Dialog>
              </Box>
            )}

            {/* Description for gift_pack */}
            {item.type === "gift_pack" && selectedVariant && (
              <Box>
                <Typography
                  variant="body2"
                  sx={{ color: "#666", textAlign: "justify" }}
                >
                  {selectedVariant.description}
                </Typography>
              </Box>
            )}

            {/* Material & Design Labels (Slightly spaced below sizes) */}
            {item.type !== "gift_pack" && selectedVariant && selectedVariant.material && selectedVariant.design &&(
              <Box sx={{ mt: 3, pt: 2, borderTop: "1px dashed #eee", display: "flex", flexDirection: "column", gap: 0.8 }}>
                <Typography variant="body2" sx={{ color: "#666" }}>
                  <strong>Material:</strong> {selectedVariant.material}
                </Typography>
                <Typography variant="body2" sx={{ color: "#666" }}>
                  <strong>Design:</strong> {selectedVariant.design}
                </Typography>
              </Box>
            )}
          </Box>

          {/* 3. PINNED FOOTER (Price & Add to Cart - Always Visible) */}
          <Box sx={{ p: 3, pt: 1.5, borderTop: "1px solid #f0f0f0", bgcolor: "#fafafa" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Box>
                <Typography variant="h2" sx={{ fontWeight: "bold", color: item.isSaleActive && selectedVariant?.salePrice ? "red" : "#57b7fb" }}>
                  {selectedVariant
                    ? item.isSaleActive && selectedVariant.salePrice
                      ? `LKR ${(selectedVariant.salePrice * quantity).toFixed(2)}`
                      : `LKR ${(selectedVariant.unitPrice * quantity).toFixed(2)}`
                    : "LKR 0.00"}
                </Typography>
                {item.isSaleActive && selectedVariant?.salePrice && (
                  <Typography variant="body2" sx={{ textDecoration: "line-through", color: "gray" }}>
                    LKR {(selectedVariant.unitPrice * quantity).toFixed(2)}
                  </Typography>
                )}
              </Box>

              {/* Quantity Selector */}
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <IconButton onClick={decreaseQuantity} disabled={quantity === 1}>
                  <RemoveIcon />
                </IconButton>
                <Typography variant="body1" sx={{ mx: 1, fontWeight: "bold" }}>
                  {quantity}
                </Typography>
                <IconButton onClick={increaseQuantity} disabled={selectedVariant ? quantity === selectedVariant.availableQuantity : false}>
                  <AddIcon />
                </IconButton>
              </Box>
            </Box>

            <Button
              variant="contained"
              color="primary"
              onClick={handleAddItem}
              disabled={!selectedVariant}
              fullWidth
              sx={{ color: "#fff", fontWeight: "600", borderRadius: "10px", py: 1.2 }}
            >
              Add to Cart <ShoppingCartOutlinedIcon sx={{ pl: "5px" }} />
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default ItemTile;
